import React, { useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const PricingSection = () => {
  const { t } = useTranslation();
  const { direction } = useLanguage();
  const [isAnnual, setIsAnnual] = useState(true);
  const navigate = useNavigate();

  const handleSignUp = () => {
    navigate('/login');
  };

  const plans = [
    {
      id: 'basic',
      name: t('pricingBasic'),
      priceMonthly: 19.99,
      priceAnnual: 199.99,
      features: [
        t('pricingFeature1'),
        t('pricingFeature2'),
        t('pricingFeature3'),
      ],
      highlight: false,
    },
    {
      id: 'standard',
      name: t('pricingStandard'),
      priceMonthly: 29.99,
      priceAnnual: 299.99,
      features: [
        t('pricingFeature1'),
        t('pricingFeature2'),
        t('pricingFeature3'),
        t('pricingFeature4'),
        t('pricingFeature5'),
      ],
      highlight: true,
    },
    {
      id: 'premium',
      name: t('pricingPremium'),
      priceMonthly: 49.99,
      priceAnnual: 499.99,
      features: [
        t('pricingFeature1'),
        t('pricingFeature2'),
        t('pricingFeature3'),
        t('pricingFeature4'),
        t('pricingFeature5'),
        t('pricingFeature6'),
      ],
      highlight: false,
    },
  ];

  return (
    <section id="pricing" className="py-20 bg-synagogue-light">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-synagogue-blue">
            {t('pricingTitle')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('pricingSubtitle')}
          </p>
        </div>

        {/* Toggle monthly/annual billing */}
        <div className="flex justify-center mb-12">
          <div className="bg-white p-1 rounded-full flex items-center shadow-md">
            <button
              className={`py-2 px-5 rounded-full text-sm font-medium transition-colors ${
                !isAnnual 
                  ? 'bg-synagogue-blue text-white' 
                  : 'bg-transparent text-gray-600'
              }`}
              onClick={() => setIsAnnual(false)}
            >
              {t('pricingMonthly')}
            </button>
            <button
              className={`py-2 px-5 rounded-full text-sm font-medium transition-colors ${
                isAnnual 
                  ? 'bg-synagogue-blue text-white' 
                  : 'bg-transparent text-gray-600'
              }`}
              onClick={() => setIsAnnual(true)}
            >
              {t('pricingAnnual')}
            </button>
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan) => (
            <div 
              key={plan.id}
              className={`bg-white rounded-xl shadow-md overflow-hidden ${
                plan.highlight 
                  ? 'transform md:-translate-y-4 ring-2 ring-synagogue-gold' 
                  : ''
              }`}
            >
              <div className={`p-6 ${plan.highlight ? 'bg-synagogue-gold bg-opacity-10' : ''}`}>
                <h3 className="text-2xl font-bold mb-2 text-synagogue-blue">
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-synagogue-blue">
                    ${isAnnual ? plan.priceAnnual : plan.priceMonthly}
                  </span>
                  <span className="text-gray-500 text-sm">
                    /{isAnnual ? t('pricingAnnual') : t('pricingMonthly')}
                  </span>
                </div>
                {isAnnual && (
                  <div className="mb-4 text-synagogue-gold font-semibold text-sm">
                    {direction === 'rtl' ? '17% הנחה על חיוב שנתי' : '17% discount with annual billing'}
                  </div>
                )}
                <Button
                  onClick={handleSignUp}
                  className={`w-full ${
                    plan.highlight
                      ? 'bg-synagogue-gold hover:bg-synagogue-blue'
                      : 'bg-synagogue-blue hover:bg-synagogue-gold'
                  } text-white font-semibold py-2 px-4 rounded-lg`}
                >
                  {t('pricingButton')}
                </Button>
              </div>
              <div className={`p-6 border-t ${direction === 'rtl' ? 'text-right' : 'text-left'}`}>
                <ul className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <svg className="text-synagogue-gold h-5 w-5 flex-shrink-0 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PricingSection; 